import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import LeadCampaignApi from "../../../../../../infrastructure/api/leadCampaignApi";
import {
  SEND_STATUS_OPTIONS,
} from "../../constants/leadCampaignEnums";

const boolFilterOptions = [
  { value: "", label: "همه" },
  { value: "true", label: "بله" },
  { value: "false", label: "خیر" },
];

const getStatusChip = (status) => {
  const match = SEND_STATUS_OPTIONS.find((o) => String(o.value) === String(status));
  return (
    <Chip
      size="small"
      label={match?.label || status}
      color={match?.color || "default"}
      variant="outlined"
    />
  );
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("fa-IR");
  } catch {
    return value;
  }
};

const CampaignRecipientsTable = ({ campaignId, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    sendStatus: "",
    registeredAfterSend: "",
    projectRequestedAfterSend: "",
    mobile: "",
  });

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const res = await LeadCampaignApi.getCampaignRecipients(campaignId, {
        page: page + 1,
        pageSize,
        sendStatus: filters.sendStatus,
        registeredAfterSend: filters.registeredAfterSend,
        projectRequestedAfterSend: filters.projectRequestedAfterSend,
        mobile: filters.mobile.trim() || undefined,
      });
      const normalized = LeadCampaignApi.normalize(res, "بارگذاری گیرندگان ناموفق بود.");
      if (normalized.error) {
        setRows([]);
        setTotal(0);
        return;
      }
      const data = normalized.data || {};
      setRows(data.items || data.Items || []);
      setTotal(data.totalCount ?? data.TotalCount ?? 0);
    } finally {
      setLoading(false);
    }
  }, [campaignId, page, pageSize, filters, refreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        گیرندگان کمپین
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>وضعیت ارسال</InputLabel>
          <Select
            label="وضعیت ارسال"
            value={filters.sendStatus}
            onChange={(e) => {
              setPage(0);
              setFilters((f) => ({ ...f, sendStatus: e.target.value }));
            }}
          >
            <MenuItem value="">همه</MenuItem>
            {SEND_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>ثبت‌نام پس از ارسال</InputLabel>
          <Select
            label="ثبت‌نام پس از ارسال"
            value={filters.registeredAfterSend}
            onChange={(e) => {
              setPage(0);
              setFilters((f) => ({ ...f, registeredAfterSend: e.target.value }));
            }}
          >
            {boolFilterOptions.map((o) => (
              <MenuItem key={o.value || "all"} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>درخواست پس از ارسال</InputLabel>
          <Select
            label="درخواست پس از ارسال"
            value={filters.projectRequestedAfterSend}
            onChange={(e) => {
              setPage(0);
              setFilters((f) => ({ ...f, projectRequestedAfterSend: e.target.value }));
            }}
          >
            {boolFilterOptions.map((o) => (
              <MenuItem key={`p-${o.value || "all"}`} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="موبایل"
          value={filters.mobile}
          onChange={(e) => setFilters((f) => ({ ...f, mobile: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && load()}
          sx={{ minWidth: 160 }}
        />
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>موبایل</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell>ارسال</TableCell>
              <TableCell>ثبت‌نام</TableCell>
              <TableCell>درخواست</TableCell>
              <TableCell>خطا</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>در حال بارگذاری...</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>گیرنده‌ای یافت نشد.</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.mobile || row.Mobile || row.maskedMobile || row.MaskedMobile || "—"}</TableCell>
                  <TableCell>{getStatusChip(row.sendStatus ?? row.SendStatus)}</TableCell>
                  <TableCell>{formatDate(row.sentAt ?? row.SentAt)}</TableCell>
                  <TableCell>
                    {(row.registeredAfterSend ?? row.RegisteredAfterSend)
                      ? formatDate(row.registeredAt ?? row.RegisteredAt)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {(row.projectRequestedAfterSend ?? row.ProjectRequestedAfterSend)
                      ? formatDate(row.projectRequestedAt ?? row.ProjectRequestedAt)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {row.errorMessage || row.ErrorMessage || "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage="تعداد در صفحه"
      />
    </Box>
  );
};

export default CampaignRecipientsTable;
